import { useReactiveVar } from '@apollo/client'
import AndroidIcon from '@mui/icons-material/Android'
import AppleIcon from '@mui/icons-material/Apple'
import { Box, styled } from '@mui/material'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { useFlag } from '@unleash/proxy-client-react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { selectModeEnabledVar } from '../../providers/apollo/cache'
import AlbumSlideshow from '../../views/account/views/gallery/components/iconButtons/AlbumSlideshow'
import MediaDetails from '../../views/account/views/gallery/components/iconButtons/MediaDetails'
import MediaDownload from '../../views/account/views/gallery/components/iconButtons/MediaDownload'
import MediaPrintShop from '../../views/account/views/gallery/components/iconButtons/MediaPrintShop'
import useSelectMedia from '../../views/account/views/gallery/hooks/useSelectMedia'

const AndroidButton = styled(IconButton)(({ theme }) => ({
  background: 'none',
  border: '1px solid #e1e1e1',
  borderLeft: 'none',
  borderRight: 'none',
  borderRadius: 0,
  color: '#000',
  margin: 0,
  padding: '0 7px',
  '&:hover': {
    backgroundColor: '#1C47CB',
    color: '#ffffff'
  },
  [theme.breakpoints.up('sm')]: {
    padding: '0 12px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '0 20px',
    fontSize: '1rem'
  },
  [theme.breakpoints.up('xl')]: {
    padding: '0 30px',
    fontSize: '1.5rem'
  }
}))

const AppleButton = styled(IconButton)(({ theme }) => ({
  background: 'none',
  border: '1px solid #e1e1e1',
  borderLeft: 'none',
  borderRadius: 0,
  color: '#000',
  margin: 0,
  padding: '0 7px',
  '&:hover': {
    backgroundColor: '#1C47CB',
    color: '#ffffff'
  },
  [theme.breakpoints.up('sm')]: {
    padding: '0 12px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '0 20px',
    fontSize: '1rem'
  },
  [theme.breakpoints.up('xl')]: {
    padding: '0 30px',
    fontSize: '1.5rem'
  }
}))

const SignUpButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(158.55deg, #202CD3 5.3%, #02E39F 102.34%)',
  fontFamily: '"Outfit", Helvetica, sans-serif',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '0 60px 60px 0',
  border: '1px solid #e1e1e1',
  color: '#fff',
  fontSize: '0.75rem',
  margin: 0,
  padding: '0 14px 0 7px',
  '&:hover': {
    backgroundColor: '#002D62',
    color: '#ffffff'
  },
  [theme.breakpoints.up('sm')]: {
    padding: '0 20px 0 12px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '0 30px 0 20px',
    fontSize: '1.125rem'
  },
  [theme.breakpoints.up('xl')]: {
    padding: '0 40px 0 30px',
    fontSize: '1.5rem'
  }
})) as typeof Button

const SignInButton = styled(Button)(({ theme }) => ({
  background: 'none',
  fontFamily: '"Outfit", Open Sans, sans-serif',
  fontWeight: 500,
  textTransform: 'none',
  border: '1px solid #e1e1e1',
  borderRadius: '60px 0 0 60px',
  color: '#1c47cb',
  fontSize: '0.75rem',
  margin: 0,
  padding: '0 7px 0 14px',
  [theme.breakpoints.up('sm')]: {
    padding: '0 12px 0 20px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '0 20px 0 30px',
    fontSize: '1.125rem'
  },
  [theme.breakpoints.up('xl')]: {
    padding: '0 30px 0 40px',
    fontSize: '1.5rem'
  }
})) as typeof Button

const SignInUpLink = styled(Link)(({ theme }) => ({
  color: '#1C47CB',
  textDecoration: 'none',
  fontWeight: 600,
  fontFamily: '"Outfit", Open Sans, sans-serif',
  [theme.breakpoints.up('sm')]: {
    padding: '0 12px 0 20px'
  },
  [theme.breakpoints.up('md')]: {
    padding: '0 20px 0 30px',
    fontSize: '1.125rem'
  },
  [theme.breakpoints.up('xl')]: {
    padding: '0 30px 0 40px',
    fontSize: '1.5rem'
  }
})) as unknown as typeof Link

const openAndroidAppDownload = () => {
  window.open(
    'https://play.google.com/store/apps/details?id=com.photobucket.android&utm_medium=web&utm_source=navBar&utm_content=deviceIcons&utm_campaign=v2_android',
    '_blank'
  )
}

const openiOSAppDownload = () => {
  window.open(
    'https://apps.apple.com/us/app/photobucket-backup/id314439840?utm_medium=web&utm_source=navBar&utm_content=deviceIcons&utm_campaign=v2_apple',
    '_blank'
  )
}

export default function UnauthenticatedMenu() {
  const { isEmpty } = useSelectMedia()
  const { selectedMedia } = useSelectMedia()
  const selectModeEnabled = useReactiveVar(selectModeEnabledVar)
  const selectMediaModeIsOn = selectModeEnabled.media || selectModeEnabled.albums || !isEmpty()
  const { userId } = useParams<{ userId?: string }>()
  const isNewCheckoutVisible = useFlag('isNewCheckoutVisible')
  const location = useLocation()
  const signUpLink = isNewCheckoutVisible ? `/auth/register${location.search}` : `/signup${location.search}`

  const { pathname } = location
  const albumId = pathname.substring(pathname.indexOf('/a/') + 3)
  const isSignUp = pathname.includes('/register')
  const isSignIn = pathname.includes('/login')

  if (isSignUp) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <SignInUpLink data-test="app-bar-sign-in" to={`/auth/login${location.search}`}>
          Sign In
        </SignInUpLink>
      </Box>
    )
  }

  if (isSignIn) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <SignInUpLink data-test="app-bar-sign-up" to={`/auth/register${location.search}`}>
          Sign Up
        </SignInUpLink>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {userId &&
        (selectMediaModeIsOn ? (
          <>
            <MediaDetails ids={selectedMedia} isPublic />
            <MediaDownload ids={selectedMedia} isUnauthenticated isPublic />
            <AlbumSlideshow albumId={albumId} username={userId} disabled={!albumId} />
            <MediaPrintShop ids={selectedMedia} isDisabled={false} />
          </>
        ) : (
          <AlbumSlideshow albumId={albumId} username={userId} disabled={false} />
        ))}
      <Box sx={{ display: 'flex' }}>
        {/* signin btn */}
        <SignInButton color="inherit" component={Link} to={`/auth/login${location.search}`} data-test="appbar-signin" id="appbar-signin">
          Sign In
        </SignInButton>

        {/* iOS btn */}
        <Tooltip
          arrow
          title="Download iOS"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#2b2b2b',
                borderRadius: '1.5rem',
                fontSize: '.85rem',
                lineHeight: 1.8,
                maxWidth: '16rem',
                padding: '1rem'
              }
            }
          }}
        >
          <AppleButton id="appbar-mobile-app-dl-apple" onClick={openiOSAppDownload}>
            <AppleIcon sx={{ fontSize: { xs: '1rem', md: '2rem' } }} />
          </AppleButton>
        </Tooltip>

        {/* google btn */}
        <Tooltip
          arrow
          title="Download Android"
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: '#2b2b2b',
                borderRadius: '1.5rem',
                fontSize: '.85rem',
                lineHeight: 1.8,
                maxWidth: '16rem',
                padding: '1rem'
              }
            }
          }}
        >
          <AndroidButton id="appbar-mobile-app-dl-android" onClick={openAndroidAppDownload}>
            <AndroidIcon sx={{ fontSize: { xs: '1rem', md: '2rem' } }} />
          </AndroidButton>
        </Tooltip>

        {/* signup btn */}
        <SignUpButton color="inherit" data-test="appbar-signup" id="appbar-signup" variant="outlined" component={Link} to={signUpLink}>
          Sign Up
        </SignUpButton>
      </Box>
    </Box>
  )
}
